///
/// EarthBrowser
///
/// Created by Matt Giger on 9/2/2024
/// Copyright (c) 2024 Geodata Labs
///


import { appStore, persistor } from '@/redux/store'
import { resetToInitialState, setCameraState, setCameraLocation, sendDatasetAction, setClockState, setClockAnimatingSpeed, setClockCurrentTime, setClockStartOffset, setClockEndOffset, type EarthBrowserState } from '@/redux/earthBrowserSlice'
import { isStateValid, validateEarthBrowserState } from '@/redux/validation/earthBrowserValidation'
import { validateSavedViewsState } from '@/redux/validation/savedViewsValidation'
import { applyValidatedSavedViewsState } from '@/redux/savedViewsSlice'
import { validatePlacemarksState } from '@/redux/validation/placemarksValidation'
import { applyValidatedPlacemarksState } from '@/redux/placemarksSlice'
import { validateEngagementState } from '@/redux/validation/engagementValidation'
import { applyValidatedEngagementState } from '@/redux/engagementSlice'
import { validateStoriesState } from '@/redux/validation/storiesValidation'
import { applyValidatedStoriesState } from '@/redux/storiesSlice'
import { beginCrashRecovery } from '@/earthbrowser/utils/CrashRecovery'
import { Provider } from 'react-redux'
import { PersistGate } from 'redux-persist/integration/react'

/**
 * Validates and repairs the persisted state before the store is ready.
 * If the state is invalid, attempts to repair it. If repair fails, resets to initial state.
 */
function validateAndRepairPersistedState() {
	const state = appStore.getState()

	if (!state.earthbrowser) {
		return
	}

	const valid = isStateValid(state.earthbrowser)

	if (!valid) {
		console.warn("[Validation] ⚠️  Invalid state detected - attempting repair")

		try {
			repairState(state.earthbrowser)
			console.info("[Validation] ✓ State repaired successfully")
		} catch (error) {
			console.error("[Validation] ❌ Repair failed, resetting to defaults:", error)
			appStore.dispatch(resetToInitialState())
			console.info("[Validation] ✓ State reset complete")
		}
	} else {
		// console.info("[Validation] ✓ State validated successfully")
	}

	// Validate the savedViews slice: drop malformed views, keep the rest
	const validatedViews = validateSavedViewsState(state.savedViews)
	if (JSON.stringify(validatedViews) !== JSON.stringify(state.savedViews)) {
		console.warn("[Validation] ⚠️  Repairing savedViews state")
		appStore.dispatch(applyValidatedSavedViewsState(validatedViews))
	}

	// Validate the placemarks slice: drop malformed placemarks, keep the rest
	const validatedPlacemarks = validatePlacemarksState(state.placemarks)
	if (JSON.stringify(validatedPlacemarks) !== JSON.stringify(state.placemarks)) {
		console.warn("[Validation] ⚠️  Repairing placemarks state")
		appStore.dispatch(applyValidatedPlacemarksState(validatedPlacemarks))
	}

	// Validate the stories slice: drop malformed stories, keep the rest
	const validatedStories = validateStoriesState(state.stories)
	if (JSON.stringify(validatedStories) !== JSON.stringify(state.stories)) {
		console.warn("[Validation] ⚠️  Repairing stories state")
		appStore.dispatch(applyValidatedStoriesState(validatedStories))
	}

	// Validate the engagement slice: repair malformed fields to defaults
	const validatedEngagement = validateEngagementState(state.engagement)
	if (JSON.stringify(validatedEngagement) !== JSON.stringify(state.engagement)) {
		console.warn("[Validation] ⚠️  Repairing engagement state")
		appStore.dispatch(applyValidatedEngagementState(validatedEngagement))
	}
}

/**
 * Repairs invalid state by validating and restoring each field.
 */
function repairState(earthbrowserState: Partial<EarthBrowserState>) {
	const repaired = validateEarthBrowserState(earthbrowserState)

	// Apply the repaired camera state
	appStore.dispatch(setCameraState(repaired.cameraState))
	appStore.dispatch(setCameraLocation(repaired.cameraLocation))

	// Apply the repaired clock state
	appStore.dispatch(setClockState(repaired.clockState))
	appStore.dispatch(setClockAnimatingSpeed(repaired.clockAnimatingSpeed))
	appStore.dispatch(setClockCurrentTime(repaired.clockCurrentTime))
	appStore.dispatch(setClockStartOffset(repaired.clockStartOffset))
	appStore.dispatch(setClockEndOffset(repaired.clockEndOffset))

	// Restore datasets
	repaired.visibleDatasets.forEach(dataset => {
		appStore.dispatch(sendDatasetAction({ type: dataset, action: 'enable' }))
	})
}

export function StoreProvider({ children }: { children: React.ReactNode }) {
	if(typeof window !== 'undefined') {

		const onBeforeLift = () => {
			validateAndRepairPersistedState()
			// After a crash (CrashSentinel verdict) heavy persisted layers come
			// back staggered instead of all at once, before the scene exists
			beginCrashRecovery()
		}

		return <Provider store={appStore}>
			<PersistGate loading={null} persistor={persistor} onBeforeLift={onBeforeLift}>
				{children}
			</PersistGate>
		</Provider>

	} else {

		return <Provider store={appStore}>
			{children}
		</Provider>
	}
}
